/**
 * HighScoreScreen - Displays top 10 high scores
 * Styled table with Rank, Name, Score, Date columns and a Back button.
 */

import { HighScoreManager } from '../utils/HighScoreManager.js';

export class HighScoreScreen {
  constructor(game) {
    this.game = game;
    this.highScoreManager = new HighScoreManager();
    this.scores = [];
    this.backHovered = false;

    // Starfield for visual consistency
    this.stars = [];
    this._initStars(80);
  }

  _initStars(count) {
    this.stars = [];
    for (let i = 0; i < count; i++) {
      this.stars.push({
        x: Math.random(),
        y: Math.random(),
        size: Math.random() * 1.5 + 0.3,
        speed: Math.random() * 0.01 + 0.003,
        brightness: Math.random() * 0.5 + 0.2,
        twinkleSpeed: Math.random() * 2 + 1,
        twinklePhase: Math.random() * Math.PI * 2,
      });
    }
  }

  enter(data = {}) {
    this.highScoreManager.refresh();
    this.scores = this.highScoreManager.getScores();
    this.backHovered = false;
    this._updateOverlay();
  }

  exit() {
    if (this.game.uiOverlay) {
      this.game.uiOverlay.innerHTML = '';
    }
  }

  update(dt) {
    const input = this.game.input;

    // Animate starfield
    for (const star of this.stars) {
      star.y += star.speed * dt;
      star.twinklePhase += star.twinkleSpeed * dt;
      if (star.y > 1) {
        star.y = 0;
        star.x = Math.random();
      }
    }

    if (input.isKeyJustPressed('Escape') || input.isKeyJustPressed('Backspace')) {
      this.game.state.setState('title');
      return;
    }
    if (input.isKeyJustPressed('Enter')) {
      this.game.state.setState('title');
      return;
    }

    // Track hover over back button
    const pos = input.getPointerPosition();
    this.backHovered = this._isOverBackButton(pos.x, pos.y);

    if (input.wasClicked()) {
      if (this._isOverBackButton(pos.x, pos.y)) {
        this.game.state.setState('title');
      }
    }
  }

  /**
   * Check if position is within the Back button hit area
   */
  _isOverBackButton(x, y) {
    const w = this.game.width;
    const h = this.game.height;
    const btnY = h * 0.9;
    const btnWidth = Math.max(150, w * 0.2); // Min 150px for touch
    const btnHeight = Math.max(44, h * 0.06); // Min 44px for touch targets
    const left = w / 2 - btnWidth / 2;
    const top = btnY - btnHeight / 2;
    return x >= left && x <= left + btnWidth && y >= top && y <= top + btnHeight;
  }

  /**
   * Update the DOM overlay with high score screen content for Playwright testing
   */
  _updateOverlay() {
    if (!this.game.uiOverlay) return;

    // Build score rows for DOM
    let rowsHtml = '';
    for (let i = 0; i < 10; i++) {
      const entry = this.scores[i];
      rowsHtml += `<div class="score-row" data-rank="${i + 1}">
        <span class="rank">${i + 1}</span>
        <span class="name">${entry ? entry.name : '---'}</span>
        <span class="score">${entry ? entry.score.toLocaleString() : '---'}</span>
        <span class="date">${entry ? entry.date : '---'}</span>
      </div>`;
    }

    this.game.uiOverlay.innerHTML = `
      <div data-screen="highScores">
        <div class="screen-title">HIGH SCORES</div>
        <div class="score-table">
          <div class="score-header">
            <span class="rank">RANK</span>
            <span class="name">NAME</span>
            <span class="score">SCORE</span>
            <span class="date">DATE</span>
          </div>
          ${rowsHtml}
        </div>
        <div class="back-button" data-action="back">BACK</div>
      </div>
    `;
  }

  render(ctx) {
    const w = this.game.width;
    const h = this.game.height;

    this.game.renderer.clearBackground();

    // Starfield background
    this._renderStarfield(ctx, w, h);

    // Title
    this.game.renderer.drawGlowText('HIGH SCORES', w / 2, h * 0.1, {
      font: `${Math.max(28, w * 0.035)}px "Share Tech Mono", monospace`,
      color: '#ffaa00',
      glowColor: '#ffaa00',
      glowSize: 15,
    });

    // Horizontal line under title
    ctx.save();
    ctx.strokeStyle = '#ffaa00';
    ctx.globalAlpha = 0.3;
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(w * 0.1, h * 0.15);
    ctx.lineTo(w * 0.9, h * 0.15);
    ctx.stroke();
    ctx.restore();

    // Table header
    const headerY = h * 0.2;
    const colRank = w * 0.15;
    const colName = w * 0.35;
    const colScore = w * 0.6;
    const colDate = w * 0.82;
    const fontSize = Math.max(14, w * 0.018);
    const rowHeight = h * 0.06;

    // Draw header with glow
    this.game.renderer.drawGlowText('RANK', colRank, headerY, {
      font: `${fontSize}px "Share Tech Mono", monospace`,
      color: '#00ffff',
      glowColor: '#00ffff',
      glowSize: 5,
    });
    this.game.renderer.drawGlowText('NAME', colName, headerY, {
      font: `${fontSize}px "Share Tech Mono", monospace`,
      color: '#00ffff',
      glowColor: '#00ffff',
      glowSize: 5,
    });
    this.game.renderer.drawGlowText('SCORE', colScore, headerY, {
      font: `${fontSize}px "Share Tech Mono", monospace`,
      color: '#00ffff',
      glowColor: '#00ffff',
      glowSize: 5,
    });
    this.game.renderer.drawGlowText('DATE', colDate, headerY, {
      font: `${fontSize}px "Share Tech Mono", monospace`,
      color: '#00ffff',
      glowColor: '#00ffff',
      glowSize: 5,
    });

    // Separator line under header
    ctx.save();
    ctx.strokeStyle = '#00ffff';
    ctx.globalAlpha = 0.2;
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(w * 0.05, headerY + rowHeight * 0.3);
    ctx.lineTo(w * 0.95, headerY + rowHeight * 0.3);
    ctx.stroke();
    ctx.restore();

    // Scores
    for (let i = 0; i < 10; i++) {
      const y = headerY + rowHeight * (i + 1);
      const entry = this.scores[i];
      const rowColor = entry ? '#ffffff' : '#667788';

      this.game.renderer.drawGlowText(`${i + 1}`, colRank, y, {
        font: `${fontSize}px "Share Tech Mono", monospace`,
        color: rowColor,
        glowColor: '#222222',
        glowSize: 0,
      });
      this.game.renderer.drawGlowText(entry ? entry.name : '---', colName, y, {
        font: `${fontSize}px "Share Tech Mono", monospace`,
        color: rowColor,
        glowColor: '#222222',
        glowSize: 0,
      });
      this.game.renderer.drawGlowText(entry ? entry.score.toLocaleString() : '---', colScore, y, {
        font: `${fontSize}px "Share Tech Mono", monospace`,
        color: rowColor,
        glowColor: '#222222',
        glowSize: 0,
      });
      this.game.renderer.drawGlowText(entry ? entry.date : '---', colDate, y, {
        font: `${fontSize}px "Share Tech Mono", monospace`,
        color: rowColor,
        glowColor: '#222222',
        glowSize: 0,
      });
    }

    // Back button with hover effect
    const backColor = this.backHovered ? '#00ffff' : '#aabbcc';
    const backGlow = this.backHovered ? 15 : 5;
    this.game.renderer.drawGlowText('[ BACK ]', w / 2, h * 0.9, {
      font: `${Math.max(16, w * 0.02)}px "Share Tech Mono", monospace`,
      color: backColor,
      glowColor: this.backHovered ? '#00ffff' : '#445566',
      glowSize: backGlow,
    });
  }

  _renderStarfield(ctx, w, h) {
    for (const star of this.stars) {
      const twinkle = 0.5 + 0.5 * Math.sin(star.twinklePhase);
      const alpha = star.brightness * twinkle;

      ctx.save();
      ctx.globalAlpha = alpha;
      ctx.fillStyle = '#ffffff';
      ctx.beginPath();
      ctx.arc(star.x * w, star.y * h, star.size, 0, Math.PI * 2);
      ctx.fill();
      ctx.restore();
    }
  }
}
